import React, { AnchorHTMLAttributes, HTMLAttributes, ReactNode, forwardRef } from "react";

import classnames from "classnames";
import { twMerge } from "tailwind-merge";

export type FooterDirection = "vertical" | "horizontal" | "responsive";

export interface FooterProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  center?: boolean;
  direction?: FooterDirection;
}

export interface FooterTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  children: ReactNode;
}

export interface FooterLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  children: ReactNode;
}

/**
 * Cobalt Footer - Based on DaisyUI footer classes
 * @see https://daisyui.com/components/footer/
 */
export const Footer = forwardRef<HTMLElement, FooterProps>(({ children, center = false, className, direction = "responsive", ...rest }, ref) => {
  const classes = twMerge(
    "si-footer",
    classnames({
      "si-footer-vertical": direction === "vertical",
      "si-footer-horizontal": direction === "horizontal",
      "sm:si-footer-horizontal": direction === "responsive",
      "si-footer-center": center,
    }),
    className,
  );

  return (
    <footer ref={ref} className={classes} {...rest}>
      {children}
    </footer>
  );
});

Footer.displayName = "Footer";

export const FooterNav = forwardRef<HTMLElement, HTMLAttributes<HTMLElement>>(({ children, className, ...rest }, ref) => (
  <nav ref={ref} className={className} {...rest}>
    {children}
  </nav>
));

FooterNav.displayName = "FooterNav";

export const FooterAside = forwardRef<HTMLElement, HTMLAttributes<HTMLElement>>(({ children, className, ...rest }, ref) => (
  <aside ref={ref} className={className} {...rest}>
    {children}
  </aside>
));

FooterAside.displayName = "FooterAside";

export const FooterTitle = forwardRef<HTMLHeadingElement, FooterTitleProps>(({ children, className, ...rest }, ref) => (
  <div ref={ref} className={twMerge("si-footer-title", className)} {...rest}>
    {children}
  </div>
));

FooterTitle.displayName = "FooterTitle";

export const FooterLink = forwardRef<HTMLAnchorElement, FooterLinkProps>(({ children, className, ...rest }, ref) => (
  <a ref={ref} className={twMerge("si-link si-link-hover text-base", className)} {...rest}>
    {children}
  </a>
));

FooterLink.displayName = "FooterLink";

export default Footer;
